import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, shareReplay, catchError, of } from 'rxjs';

export interface PhonePrefixCountry {
  code: string;
  name: string;
  dial_code: string;
}

@Injectable({
  providedIn: 'root'
})
export class PhonePrefixService {
  private phonePrefixes$: Observable<PhonePrefixCountry[]> | null = null;
  private readonly API_URL = '/assets/json/phonePrefixCountries.json';

  constructor(private http: HttpClient) {}

  getPhonePrefixes(): Observable<PhonePrefixCountry[]> {
    this.phonePrefixes$ = this.http.get<PhonePrefixCountry[]>(this.API_URL).pipe(
      map(data => {
        // 1. Eliminar duplicados usando Map (más eficiente para datos grandes)
        const uniqueMap = new Map();
        data.forEach(item => {
          if (!uniqueMap.has(item.code)) {
            uniqueMap.set(item.code, item);
          }
        });
        const uniqueData = Array.from(uniqueMap.values());

        // 2. Filtrar elementos con dial_code válido
        const filteredData = uniqueData.filter(item =>
          item.dial_code != null && item.dial_code.trim() !== ""
        );

        // 3. Ordenar por nombre del país (case insensitive)
        return filteredData.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        );
      }),
      shareReplay(1),
      catchError((error) => {
        console.error('Error loading phone prefixes:', error);
        return of([]);
      })
    );
    return this.phonePrefixes$;
  }

  getPhonePrefixOptions(): Observable<{dial_code: string, display: string}[]> {
    return this.getPhonePrefixes().pipe(
      map(prefixes => {
        const options = prefixes.map(prefix => ({
          dial_code: prefix.dial_code,
          display: `(${prefix.dial_code}) ${prefix.name}`
        }));
        return [{dial_code: '-1', display: '---'}, ...options];
      })
    );
  }
}
